import { z } from 'zod';

import { immediate } from '@tdp/common/shared';

import {
  IUserPreferenceReader,
  IUserPreferenceWriter,
  PreferenceReadResult,
} from './abstractions';

export interface LocalStorageUserPreferenceStoreOptions<
  TKey extends string,
  TPreference extends z.ZodTypeAny,
> {
  storageKey: TKey;
  schema: TPreference;
  serializer?: (preference: TPreference) => string;
  deserializer?: (rawValue: string) => unknown;
}

export class LocalStorageUserPreferenceStore<
    TKey extends string,
    TPreference extends z.ZodTypeAny,
  >
  implements
    IUserPreferenceReader<TPreference>,
    IUserPreferenceWriter<z.infer<TPreference>>
{
  private storageKey: TKey;
  private schema: TPreference;
  private serializer: (preference: TPreference) => string;
  private deserializer: (rawValue: string) => unknown;

  public constructor(
    options: LocalStorageUserPreferenceStoreOptions<TKey, TPreference>
  ) {
    this.storageKey = options.storageKey;
    this.schema = options.schema;
    // by default assume this is a complex type
    this.serializer = options.serializer ?? ((v) => JSON.stringify(v));
    this.deserializer =
      options.deserializer ?? ((v) => JSON.parse(v) as unknown);
  }

  async writePreference(preference: z.infer<TPreference>): Promise<void> {
    await immediate;
    const serialized = this.serializer(preference);
    localStorage.setItem(this.storageKey, serialized);
  }

  async readPreference(): Promise<PreferenceReadResult<z.infer<TPreference>>> {
    await immediate;
    const rawValue = localStorage.getItem(this.storageKey);
    if (!rawValue) {
      return { type: 'failure', reason: 'notFound' };
    }
    const deserialized = this.deserializer(rawValue);
    const preference = this.schema.safeParse(deserialized);
    if (!preference.success) {
      return {
        type: 'failure',
        reason: 'parseError',
        errorMessage: preference.error.message,
      };
    }
    //TODO(nibeauli): figure out why type inference is failing here
    return { type: 'success', value: preference.data as z.infer<TPreference> };
  }
}
